import { type CampoNumero, type Respuesta, respuestaEsValida } from '@pasos/schemas';
import { useId, useState } from 'react';
import { PACIENTE } from '../../copy/index.js';
import { LOCALE } from '../../lib/locale.js';

type NumeroProps = {
  campo: CampoNumero;
  valor: number | undefined;
  /** `undefined` cuando lo escrito todavía no es una respuesta válida. */
  onElegir: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
};

/** Un número con coma decimal, como se escribe en la Argentina. */
export function formatearNumero(valor: number): string {
  return valor.toLocaleString(LOCALE, { maximumFractionDigits: 3 });
}

/**
 * Lo escrito, como número. Acepta coma o punto decimal y nada más: "500 mg" o
 * "dos" no son un número, y no se adivinan.
 */
export function leerNumero(texto: string): number | null {
  const limpio = texto.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(limpio)) return null;
  return Number(limpio);
}

function problemaDe(campo: CampoNumero, texto: string): string | null {
  if (texto.trim() === '') return null;
  const unidad = campo.unidad.etiqueta;
  const numero = leerNumero(texto);
  const [minimo, maximo] = [formatearNumero(campo.minimo), formatearNumero(campo.maximo)];
  if (numero === null || numero < campo.minimo || numero > campo.maximo) {
    return PACIENTE.formulario.numeroInvalido(minimo, maximo, unidad);
  }
  if (!respuestaEsValida(campo, numero)) return PACIENTE.formulario.decimalesDeMas(campo.decimales);
  return null;
}

/**
 * Una cantidad con su unidad (E3.25): la potencia en miligramos, el intervalo en
 * horas.
 *
 * **Teclado numérico y la unidad siempre a la vista.** `inputMode` pide el
 * teclado de números en el teléfono (con coma cuando admite decimales) y la
 * unidad va pegada a la caja, no en el enunciado: quien escribe "500" tiene que
 * ver que son miligramos. Es un `input` de texto y no `type="number"`: el
 * numérico del navegador acepta "1e3", cambia el valor con la rueda del mouse y
 * no sabe de la coma decimal.
 *
 * Lo escrito es de la pantalla; la respuesta sale recién cuando es un número del
 * rango. Mientras no lo sea, el botón de seguir no se habilita y el error dice
 * por qué, sin rojo del semáforo (`destructive`).
 */
export function Numero({ campo, valor, onElegir, etiquetadoPor }: NumeroProps) {
  const id = useId();
  const [texto, setTexto] = useState(valor === undefined ? '' : formatearNumero(valor));
  const problema = problemaDe(campo, texto);
  const unidad = campo.unidad.etiqueta;

  function cambiar(nuevo: string) {
    setTexto(nuevo);
    const numero = leerNumero(nuevo);
    onElegir(numero !== null && respuestaEsValida(campo, numero) ? numero : undefined);
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex min-h-15 items-center gap-3 rounded-lg bg-card px-4 outline-2 -outline-offset-2 focus-within:shadow-halo-navy-100 focus-within:outline-ring ${
          problema ? 'outline-destructive' : 'outline-input'
        }`}
      >
        <input
          aria-describedby={`${id}-rango${problema ? ` ${id}-error` : ''}`}
          aria-invalid={problema ? true : undefined}
          aria-labelledby={etiquetadoPor}
          autoComplete="off"
          className="min-w-0 flex-1 border-0 bg-transparent text-2xl font-bold text-heading outline-none focus-visible:outline-none"
          inputMode={campo.decimales === 0 ? 'numeric' : 'decimal'}
          onChange={(evento) => cambiar(evento.target.value)}
          type="text"
          value={texto}
        />
        <span className="shrink-0 text-xl font-bold text-subtle">{unidad}</span>
      </div>
      <p className="m-0 text-base text-subtle" id={`${id}-rango`}>
        {PACIENTE.formulario.rango(
          formatearNumero(campo.minimo),
          formatearNumero(campo.maximo),
          unidad,
        )}
      </p>
      {problema ? (
        <p className="m-0 text-base font-bold text-destructive-fg" id={`${id}-error`} role="alert">
          {problema}
        </p>
      ) : null}
    </div>
  );
}
